import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Contact } from '@ourcove/types';
import { api } from '../lib/api';
import { useStore } from '../lib/store';
import { subscribeToPush, unsubscribeFromPush } from '../lib/push';
import {
  RINGTONES,
  type RingtoneId,
  getRingtoneId,
  setRingtoneId,
  getRingtone,
  playPreview,
} from '../lib/ringtones';

const TONES = ['clay', 'water', 'sage', 'plum', 'dune'] as const;
function toneFor(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return TONES[Math.abs(h) % TONES.length];
}
function initials(name: string): string {
  return name.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}
function fmtRoleLabel(role: string): string {
  if (role === 'owner') return 'Owner';
  if (role === 'admin') return 'Admin';
  if (role === 'super') return 'Super';
  return 'Member';
}
function fmtDaysUntil(ts: number): string {
  const days = Math.max(0, Math.round((ts - Date.now()) / (24 * 3600 * 1000)));
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

function BackIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 6l-6 6 6 6" />
    </svg>
  );
}
function Chevron() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12l5 5 9-11" />
    </svg>
  );
}
function PlayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

type Sheet = 'profile' | 'members' | 'invites' | 'ringtone' | 'privacy' | null;

export function Settings() {
  const nav = useNavigate();
  const user = useStore((s) => s.user);
  const cove = useStore((s) => s.cove);
  const contacts = useStore((s) => s.contacts);
  const clearAuth = useStore((s) => s.clearAuth);
  const [pushOn, setPushOn] = useState<boolean>(() => false);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [ringtoneId, setRingId] = useState<RingtoneId>(() => getRingtoneId());

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.ready.then((reg) =>
      reg.pushManager.getSubscription().then((sub) => setPushOn(!!sub)),
    );
  }, []);

  async function togglePush() {
    if (pushOn) {
      const ok = await unsubscribeFromPush();
      if (ok) setPushOn(false);
    } else {
      const ok = await subscribeToPush();
      setPushOn(ok);
      if (!ok) alert("Notifications weren't enabled. On iPhone, first add to Home Screen.");
    }
  }

  async function signOut() {
    await api.logout().catch(() => {});
    clearAuth();
    nav('/login');
  }

  return (
    <div className="app">
      <header className="chat-header" style={{ justifyContent: 'space-between' }}>
        <Link to="/" className="back" aria-label="Back">
          <BackIcon />
        </Link>
        <h1 style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--fs-md)', fontWeight: 700, margin: 0 }}>Settings</h1>
        <div style={{ width: 22 }} />
      </header>

      <div style={{ padding: 'var(--s-5)', display: 'flex', flexDirection: 'column', gap: 0 }}>
        {/* Profile card */}
        <div className="settings-profile">
          <div className="avatar xl" data-tone={user ? toneFor(user.id) : 'clay'}>
            {user ? initials(user.displayName) : '?'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 700, color: 'var(--ink-900)' }}>
              {user?.displayName ?? '—'}
            </div>
            <div className="muted small" style={{ marginTop: 2 }}>
              {fmtRoleLabel(user?.role ?? 'member')} of {cove?.name ?? 'your cove'}
            </div>
            <button className="link-btn" onClick={() => setSheet('profile')}>
              Edit your profile →
            </button>
          </div>
        </div>

        <div className="settings-group-label">Your cove</div>
        <ul className="settings-group">
          {(user?.role === 'admin' || user?.role === 'owner') && (
            <li className="settings-row" role="link" onClick={() => nav('/admin')}>
              <span>Cove admin</span>
              <span className="muted">Members · settings</span>
              <Chevron />
            </li>
          )}
          <li className="settings-row" role="link" onClick={() => setSheet('members')}>
            <span>Members</span>
            <span className="muted">{contacts.length + 1} · View</span>
            <Chevron />
          </li>
          <li className="settings-row" role="link" onClick={() => setSheet('invites')}>
            <span>Pending invites</span>
            <span className="muted">Manage</span>
            <Chevron />
          </li>
          <li className="settings-row muted-row">
            <span>Sitter &amp; caregiver passes</span>
            <span className="pill sage">Coming soon</span>
          </li>
        </ul>

        <div className="settings-group-label">Notifications</div>
        <ul className="settings-group">
          <li className="settings-row">
            <span>Push notifications</span>
            <span className="spacer" />
            <button
              className={`toggle ${pushOn ? 'on' : ''}`}
              onClick={togglePush}
              aria-label="Toggle push notifications"
              aria-pressed={pushOn}
            >
              <span className="thumb" />
            </button>
          </li>
          <li className="settings-row" role="link" onClick={() => setSheet('ringtone')}>
            <span>Ringtone for calls</span>
            <span className="muted">{getRingtone(ringtoneId).name}</span>
            <Chevron />
          </li>
          <li className="settings-row muted-row">
            <span>Quiet hours</span>
            <span className="pill sage">Coming soon</span>
          </li>
        </ul>

        <div className="settings-group-label">About</div>
        <ul className="settings-group">
          <li className="settings-row" role="link" onClick={() => setSheet('privacy')}>
            <span>Privacy &amp; data</span>
            <Chevron />
          </li>
          <li className="settings-row muted-row">
            <span>Version</span>
            <span className="muted">OurCove 1.0</span>
          </li>
        </ul>

        <button
          onClick={signOut}
          style={{
            marginTop: 'var(--s-6)',
            background: 'transparent',
            color: 'var(--danger)',
            border: 'none',
            fontWeight: 700,
            fontSize: 'var(--fs-md)',
            padding: 'var(--s-3)',
          }}
        >
          Sign out
        </button>
      </div>

      {sheet === 'profile' && <ProfileSheet onClose={() => setSheet(null)} />}
      {sheet === 'members' && <MembersSheet onClose={() => setSheet(null)} />}
      {sheet === 'invites' && <InvitesSheet onClose={() => setSheet(null)} />}
      {sheet === 'ringtone' && (
        <RingtoneSheet
          current={ringtoneId}
          onChoose={(id) => {
            setRingtoneId(id);
            setRingId(id);
          }}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet === 'privacy' && <PrivacySheet onClose={() => setSheet(null)} />}
    </div>
  );
}

// ─── Sheets ────────────────────────────────────────────────────────

function SheetShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" />
        <h3 className="display" style={{ margin: 0, fontSize: 'var(--fs-xl)' }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function ProfileSheet({ onClose }: { onClose: () => void }) {
  const user = useStore((s) => s.user);
  const updateUser = useStore((s) => s.updateUser);
  const [name, setName] = useState(user?.displayName ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    const trimmed = name.trim();
    if (!trimmed) { setErr('Name cannot be empty.'); return; }
    if (trimmed === user?.displayName) { onClose(); return; }
    setBusy(true);
    setErr(null);
    try {
      const { user: updated } = await api.updateProfile(trimmed);
      updateUser({ displayName: updated.displayName });
      onClose();
    } catch (e: any) {
      setErr(e?.message ?? 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SheetShell title="Edit your profile" onClose={onClose}>
      <p className="muted small" style={{ marginTop: 0 }}>
        This is how your family sees you in the cove.
      </p>
      <label className="field">
        <span className="field-label">Display name</span>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={64}
          autoFocus
        />
      </label>
      {err && <div className="error" style={{ marginTop: 4 }}>{err}</div>}
      <button className="btn block" onClick={save} disabled={busy}>
        {busy ? 'Saving…' : 'Save'}
      </button>
      <button className="btn secondary block" onClick={onClose}>Cancel</button>
    </SheetShell>
  );
}

function MembersSheet({ onClose }: { onClose: () => void }) {
  const user = useStore((s) => s.user);
  const cove = useStore((s) => s.cove);
  const contacts = useStore((s) => s.contacts);
  const rows: Array<{ id: string; name: string; role: string; online: boolean; isSelf: boolean }> = useMemo(() => {
    const self = user
      ? [{
          id: user.id,
          name: user.displayName + ' (you)',
          role: user.role,
          online: true,
          isSelf: true,
        }]
      : [];
    const others = contacts.map((c: Contact) => ({
      id: c.id,
      name: c.displayName,
      role: c.role,
      online: c.online,
      isSelf: false,
    }));
    // Owner/admin first, then the rest by name.
    const rank = (r: string) => (r === 'owner' ? 0 : r === 'admin' ? 1 : 2);
    return [...self, ...others].sort((a, b) => rank(a.role) - rank(b.role) || a.name.localeCompare(b.name));
  }, [user, contacts]);

  return (
    <SheetShell title={cove?.name ?? 'Members'} onClose={onClose}>
      <p className="muted small" style={{ marginTop: 0 }}>
        Everyone in your cove.
      </p>
      <ul className="member-list">
        {rows.map((r) => (
          <li key={r.id} className="member-list-row">
            <div className="avatar md" data-tone={toneFor(r.id)}>
              {initials(r.name)}
              <span className={`dot sm ${r.online ? 'online' : ''}`} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="member-list-name">{r.name}</div>
              <div className="muted small">
                {fmtRoleLabel(r.role)}
                {r.online ? ' · Online' : ''}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <button className="btn secondary block" onClick={onClose}>Done</button>
    </SheetShell>
  );
}

function InvitesSheet({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<Array<{ token: string; note: string | null; expiresAt: number; url: string }> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busyToken, setBusyToken] = useState<string | null>(null);

  useEffect(() => {
    api.listMyInvites()
      .then(setRows)
      .catch((e) => setErr(e?.message ?? 'Could not load'));
  }, []);

  async function revoke(token: string) {
    setBusyToken(token);
    try {
      await api.revokeInvite(token);
      setRows((r) => (r ? r.filter((i) => i.token !== token) : r));
    } catch (e: any) {
      setErr(e?.message ?? 'Could not revoke');
    } finally {
      setBusyToken(null);
    }
  }

  async function copy(url: string) {
    try { await navigator.clipboard.writeText(url); } catch {}
  }

  return (
    <SheetShell title="Pending invites" onClose={onClose}>
      <p className="muted small" style={{ marginTop: 0 }}>
        Links you've created that no one has opened yet. Revoke any you don't want to be used.
      </p>
      {err && <div className="error">{err}</div>}
      {rows === null ? (
        <div className="muted small">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="muted small" style={{ padding: 'var(--s-4) 0' }}>
          No pending invites. Send one from the home screen.
        </div>
      ) : (
        <ul className="invite-list">
          {rows.map((r) => (
            <li key={r.token} className="invite-list-row">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="invite-list-note">{r.note || 'No note'}</div>
                <div className="muted small">Expires {fmtDaysUntil(r.expiresAt)}</div>
              </div>
              <button
                className="btn secondary"
                style={{ minHeight: 36, padding: '6px 12px', fontSize: 13 }}
                onClick={() => copy(r.url)}
              >
                Copy link
              </button>
              <button
                className="btn ghost"
                style={{ minHeight: 36, padding: '6px 12px', fontSize: 13, color: 'var(--danger)' }}
                onClick={() => revoke(r.token)}
                disabled={busyToken === r.token}
              >
                {busyToken === r.token ? '…' : 'Revoke'}
              </button>
            </li>
          ))}
        </ul>
      )}
      <button className="btn secondary block" onClick={onClose}>Close</button>
    </SheetShell>
  );
}

function RingtoneSheet({
  current,
  onChoose,
  onClose,
}: {
  current: RingtoneId;
  onChoose: (id: RingtoneId) => void;
  onClose: () => void;
}) {
  const [busyId, setBusyId] = useState<RingtoneId | null>(null);

  async function preview(id: RingtoneId) {
    setBusyId(id);
    try { await playPreview(id); } finally { setBusyId(null); }
  }

  return (
    <SheetShell title="Ringtone for calls" onClose={onClose}>
      <p className="muted small" style={{ marginTop: 0 }}>
        Played on this device when someone calls you.
      </p>
      <ul className="ringtone-list">
        {RINGTONES.map((r) => (
          <li
            key={r.id}
            className={`ringtone-row ${current === r.id ? 'chosen' : ''}`}
            onClick={() => onChoose(r.id)}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="ringtone-name">{r.name}</div>
              <div className="muted small">{r.description}</div>
            </div>
            <button
              className="btn ghost"
              style={{ minHeight: 36, padding: '6px 10px' }}
              onClick={(e) => { e.stopPropagation(); preview(r.id); }}
              disabled={busyId === r.id || r.id === 'silent'}
              aria-label={`Preview ${r.name}`}
            >
              <PlayIcon />
            </button>
            <span className="ringtone-check" aria-hidden>
              {current === r.id ? <CheckIcon /> : null}
            </span>
          </li>
        ))}
      </ul>
      <button className="btn block" onClick={onClose}>Done</button>
    </SheetShell>
  );
}

function PrivacySheet({ onClose }: { onClose: () => void }) {
  const user = useStore((s) => s.user);
  const cove = useStore((s) => s.cove);
  return (
    <SheetShell title="Privacy & data" onClose={onClose}>
      <p style={{ marginTop: 0, lineHeight: 1.5 }}>
        OurCove is a private space for your cove. What we store, and where:
      </p>
      <ul className="privacy-list">
        <li>
          <strong>Your account</strong>
          <span className="muted small">Email, display name, hashed password. Used to sign you in.</span>
        </li>
        <li>
          <strong>Messages &amp; photos</strong>
          <span className="muted small">Kept in your cove's private database on our server. Never sold or shared.</span>
        </li>
        <li>
          <strong>Calls</strong>
          <span className="muted small">Direct peer-to-peer when possible. Audio/video never touches our servers except through TURN relay when your network requires it.</span>
        </li>
        <li>
          <strong>Push notifications</strong>
          <span className="muted small">Your device's push endpoint, if you turn notifications on.</span>
        </li>
      </ul>
      <p className="muted small" style={{ marginTop: 'var(--s-4)' }}>
        Signed in as <strong>{user?.displayName}</strong> in <strong>{cove?.name}</strong>.
      </p>
      {user?.role === 'owner' && (
        <p className="muted small">
          As the owner, you can delete this cove and all its data from{' '}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); onClose(); window.location.assign('/admin'); }}
            style={{ color: 'var(--clay-500)', fontWeight: 700 }}
          >
            Cove admin
          </a>.
        </p>
      )}
      <button className="btn secondary block" onClick={onClose}>Close</button>
    </SheetShell>
  );
}
